import React, { useState, useRef, useEffect } from "react";
import {useFetchAllObjsWithRefetchCapability} from "../../platform/hooks/PlatformTypeScriptHooks";
import UboldSpinner from "../../ubold-components/UboldSpinner";
import {Card} from "components/Card";
import {TechDrawOverviewReportEntry, TechDrawLabelRule} from "./BrowseTechDrawsPage";
import {useNavigate, useParams} from "react-router-dom";
import AjaxService from "../../platform/ajax/AjaxService";
import {useAuthenticatedImage} from "../../hooks/useAuthenticatedImage";

type LabelTemplate = {
    sourceIdentifier: string;
    textAlignment: 'LEFT' | 'CENTER' | 'RIGHT';
    textDirection: 'HORIZONTAL' | 'VERTICAL';
};

const LABEL_TEMPLATES: LabelTemplate[] = [
    // Impeller view
    { sourceIdentifier: "CASING_LABEL", textAlignment: "CENTER", textDirection: "VERTICAL" },
    { sourceIdentifier: "BETA_B_LABEL", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "X_LABEL", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "IMP_DIAMETER", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "DISC_DIAMETER", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "BOLTS_M", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "CLOCKWISE_LABEL1", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "CLOCKWISE_LABEL2", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "IMPELLER_HEIGHT", textAlignment: "CENTER", textDirection: "VERTICAL" },
    { sourceIdentifier: "NOTE3_LABEL", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "DISC_THICKNESS", textAlignment: "CENTER", textDirection: "VERTICAL" },
    { sourceIdentifier: "X1", textAlignment: "CENTER", textDirection: "VERTICAL" },
    { sourceIdentifier: "X2", textAlignment: "CENTER", textDirection: "VERTICAL" },
    { sourceIdentifier: "Y1", textAlignment: "CENTER", textDirection: "VERTICAL" },
    { sourceIdentifier: "Y2", textAlignment: "CENTER", textDirection: "VERTICAL" },
    { sourceIdentifier: "AIRFLOW_LABEL1", textAlignment: "CENTER", textDirection: "VERTICAL" },
    { sourceIdentifier: "AIRFLOW_LABEL2", textAlignment: "CENTER", textDirection: "VERTICAL" },

    // Clutch view
    { sourceIdentifier: "C_DETAIL_X_LABEL", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "C_FIXING_BOLT_M", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "C_SHAFT_LENGTH", textAlignment: "CENTER", textDirection: "VERTICAL" },
    { sourceIdentifier: "C_CLUTCH_EXT_DIAM", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "C_DRIVE_SHAFT_LABEL", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "C_SECPAD_THICKNESS", textAlignment: "CENTER", textDirection: "VERTICAL" },
    { sourceIdentifier: "C_SECPAD_HOLE_DIAM", textAlignment: "CENTER", textDirection: "HORIZONTAL" },

    // Shaft view
    { sourceIdentifier: "SH_Y_Y_LABEL", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "SH_WPUST_K", textAlignment: "CENTER", textDirection: "VERTICAL" },
    { sourceIdentifier: "SH_FG", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "SH_J", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "SH_DISC_DETAIL_LABEL", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "SH_DISC_HOLE_TOLERANCE", textAlignment: "CENTER", textDirection: "HORIZONTAL" },
    { sourceIdentifier: "SH_DISC_MOUNTING_HOLES", textAlignment: "CENTER", textDirection: "HORIZONTAL" },

];

// Extracted table component
interface TechDrawRulesTableProps {
    rules: TechDrawLabelRule[];
    onRemoveLabel: (index: number) => void;
    onUpdateFontSize: (index: number, fontSize: number) => void;
}

const TechDrawRulesTable: React.FC<TechDrawRulesTableProps> = ({ rules, onRemoveLabel, onUpdateFontSize }) => {
    if (rules.length === 0) {
        return <div className="text-muted">Brak reguł etykiet. Dodaj etykiety z przycisków po lewej stronie.</div>;
    }

    return (
        <div className="table-responsive">
            <table className="table table-bordered table-sm">
                <thead>
                    <tr>
                        <th>Etykieta</th>
                        <th>X,Y</th>

                        <th>Mnożnik w. czcionki</th>
                        <th>Akcje</th>
                    </tr>
                </thead>
                <tbody>
                    {rules.map((rule, index) => (
                        <tr key={index}>
                            <td>{rule.sourceIdentifier}</td>
                            <td>{rule.xPosition.toFixed(3)}<br/>
                            {rule.yPosition.toFixed(3)}</td>
                            <td>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    style={{ width: '80px' }}
                                    value={rule.relativeFontSize ?? 1.0}
                                    step="0.1"
                                    min="0.1"
                                    max="5.0"
                                    onChange={(e) => {
                                        const value = parseFloat(e.target.value);
                                        if (!isNaN(value)) {
                                            onUpdateFontSize(index, value);
                                        }
                                    }}
                                />
                            </td>
                            <td>
                                <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => onRemoveLabel(index)}
                                >
                                    Usuń
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Draggable Label Component
interface DraggableLabelProps {
    rule: TechDrawLabelRule;
    index: number;
    isDragged: boolean;
    onMouseDown: () => void;
    imageWidth: number;
    imageHeight: number;
}

const DraggableLabel: React.FC<DraggableLabelProps> = ({ 
    rule, 
    index, 
    isDragged, 
    onMouseDown,
    imageWidth,
    imageHeight
}) => {
    // Convert relative coordinates (0.0-1.0) to absolute pixel positions for rendering
    const absoluteX = Math.round(rule.xPosition * imageWidth);
    const absoluteY = Math.round(rule.yPosition * imageHeight);
    
    return (
        <div 
            key={`${rule.sourceIdentifier}-${index}`}
            className="position-absolute"
            style={{
                left: `${absoluteX}px`,
                top: `${absoluteY}px`,
                transform: "translate(-50%, -50%)",
                cursor: "move",
                userSelect: "none",
                border: isDragged ? "2px solid yellow" : "none",
                zIndex: isDragged ? 1000 : 100
            }}
            onMouseDown={onMouseDown}
        >
            {/* Anchor point marker (blue circle) */}
            <div 
                style={{
                    position: 'absolute',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0,0,255,0.45)',
                    transform: 'translate(-50%, -50%)',
                    left: '50%',
                    top: '50%',
                    zIndex: 2
                }}
            />
            
            {/* Label text */}
            <div 
                className="d-flex align-items-center justify-content-center"
                style={{
                    padding: "2px 3px",
                    background: "rgba(0, 123, 255, 0.4)",
                    color: "black",
                    borderRadius: "2px",
                    transform: rule.textDirection === 'VERTICAL' ? 'rotate(-90deg)' : 'none'
                }}
            >
                {rule.sourceIdentifier}
            </div>
        </div>
    );
};

export function EditTechDrawPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [rules, setRules] = useState<TechDrawLabelRule[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [draggedItem, setDraggedItem] = useState<number | null>(null);
    const [displayName, setDisplayName] = useState<string>("");
    const [previewTimestamp, setPreviewTimestamp] = useState<number | null>(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [rulesLoaded, setRulesLoaded] = useState(false);
    const [imageTimestamp, setImageTimestamp] = useState(Date.now()); // Add timestamp for cache busting
    const imageRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    
    const { result: techDraw, refetch } = useFetchAllObjsWithRefetchCapability<TechDrawOverviewReportEntry>(`/api/tech-draws/${id}`);

    const rawImageObjectUrl = useAuthenticatedImage(id ? `/api/tech-draws/${id}/raw-image?t=${imageTimestamp}` : null);
    const previewObjectUrl = useAuthenticatedImage(id && previewTimestamp ? `/api/tech-draws/${id}/generate-labeled-image?t=${previewTimestamp}` : null);

    // Initialize rules state when tech draw data is loaded
    useEffect(() => {
        if (techDraw && techDraw.rules) {
            console.log('Rules from backend:', JSON.stringify(techDraw.rules, null, 2));

            // Map the rules, assuming they are already in relative format (0.0-1.0)
            const mappedRules = techDraw.rules.map(rule => ({
                sourceIdentifier: rule.sourceIdentifier,
                textAlignment: rule.textAlignment,
                textDirection: rule.textDirection,
                xPosition: rule.xPosition,
                yPosition: rule.yPosition,
                relativeFontSize: rule.relativeFontSize ?? 1.0
            } as TechDrawLabelRule));

            console.log('Mapped rules (relative format):', mappedRules);
            setRules(mappedRules);
            setRulesLoaded(true);

            // Initialize display name from tech draw data
            if (techDraw.displayName) {
                setDisplayName(techDraw.displayName);
            }
        }
    }, [techDraw]);

    // Handle image load event to ensure proper label positioning
    const handleImageLoad = () => {
        console.log('Image loaded with dimensions:', {
            width: imageRef.current?.offsetWidth,
            height: imageRef.current?.offsetHeight
        });
        setImageLoaded(true);
    };

    // Force re-render of labels when both image and rules are loaded
    useEffect(() => {
        if (imageLoaded && rulesLoaded) {
            console.log('Both image and rules are loaded, ensuring proper label positioning');
            // This is just to trigger a re-render with the correct dimensions
            setRules(prevRules => [...prevRules]);
        }
    }, [imageLoaded, rulesLoaded]);

    const handleBack = () => {
        navigate("/browse-tech-draws");
    };

    // Check if a label with the given sourceIdentifier already exists
    const labelExists = (sourceIdentifier: string): boolean => {
        return rules.some(rule => rule.sourceIdentifier === sourceIdentifier);
    };

    const addNewLabel = (template: LabelTemplate) => {
        if (labelExists(template.sourceIdentifier)) {
            console.log(`Label with sourceIdentifier "${template.sourceIdentifier}" already exists.`);
            return; // Don't add duplicate labels
        }

        const newRule: TechDrawLabelRule = {
            sourceIdentifier: template.sourceIdentifier,
            textAlignment: template.textAlignment,
            textDirection: template.textDirection,
            xPosition: 0.5, // Center horizontally (relative coordinate)
            yPosition: 0.5,  // Center vertically (relative coordinate)
            relativeFontSize: 1.0
        };

        setRules([...rules, newRule]);
    };

    const removeLabel = (index: number) => {
        const updatedRules = [...rules];
        updatedRules.splice(index, 1);
        setRules(updatedRules);
    };

    const updateFontSize = (index: number, fontSize: number) => {
        const updatedRules = [...rules];
        updatedRules[index] = {
            ...updatedRules[index],
            relativeFontSize: fontSize
        };
        setRules(updatedRules);
    };

    const handleDragStart = (index: number) => {
        setDraggedItem(index);
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
    };

    const handleLabelMove = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
        if (draggedItem !== index) return;
        
        const container = containerRef.current;
        const imageElement = imageRef.current;
        
        if (!container || !imageElement) return;

        const rect = container.getBoundingClientRect();
        const x = Math.max(0, Math.min(e.clientX - rect.left, imageElement.offsetWidth));
        const y = Math.max(0, Math.min(e.clientY - rect.top, imageElement.offsetHeight));

        // Convert absolute pixel positions to relative coordinates (0.0-1.0)
        const relativeX = imageElement.offsetWidth > 0 ? x / imageElement.offsetWidth : 0;
        const relativeY = imageElement.offsetHeight > 0 ? y / imageElement.offsetHeight : 0;

        const updatedRules = [...rules];
        updatedRules[index] = {
            ...updatedRules[index],
            xPosition: relativeX,
            yPosition: relativeY
        };
        
        setRules(updatedRules);
    };

    const handleSave = async () => {
        if (!techDraw || !id) return;
        
        setIsSaving(true);
        
        try {
            // Send rules with relative coordinates (0.0-1.0) to the backend
            const backendRules = rules.map(rule => ({
                sourceIdentifier: rule.sourceIdentifier,
                textAlignment: rule.textAlignment,
                textDirection: rule.textDirection,
                xPosition: rule.xPosition, // Already in relative format (0.0-1.0)
                yPosition: rule.yPosition,  // Already in relative format (0.0-1.0)
                relativeFontSize: rule.relativeFontSize ?? 1.0
            }));

            console.log('Sending rules to backend (relative coordinates):', JSON.stringify(backendRules, null, 2));
            
            await AjaxService.postJson(`/api/tech-draws/${id}/update?displayName=${encodeURIComponent(displayName)}`, backendRules);
            
            console.log('Save successful, refetching data...');
            
            // Reset state before refetching to ensure proper reloading
            setImageLoaded(false);
            setRulesLoaded(false);
            setImageTimestamp(Date.now()); // Update timestamp to force image reload
            
            // Refetch the tech draw to get the updated data
            refetch();
            
            alert("Zapisano pomyślnie!");
        } catch (error) {
            console.error("Error saving tech draw:", error);
            alert("Wystąpił błąd podczas zapisywania!");
        } finally {
            setIsSaving(false);
        }
    };

    if (techDraw === null) {
        return <Card><UboldSpinner/></Card>
    }

    return (
        <>
            <Card header={`Rysunek techniczny: ${displayName}`}>
                <div className="row">
                    <div className="col-md-8">
                        <div 
                            ref={containerRef}
                            className="position-relative" 
                            style={{ 

                                overflow: "hidden",
                                userSelect: "none"
                            }}
                            onMouseMove={(e) => {
                                if (draggedItem !== null) {
                                    handleLabelMove(e, draggedItem);
                                }
                            }}
                            onMouseUp={handleDragEnd}
                        >
                            <img
                                ref={imageRef}
                                src={rawImageObjectUrl ?? undefined}
                                alt={displayName}
                                className="img-fluid"
                                draggable={false}
                                onLoad={handleImageLoad}
                                style={{border: "5px solid #ddd"}}
                            />
                            
                            {imageLoaded && rulesLoaded && rules.map((rule, index) => (
                                <DraggableLabel
                                    key={index}
                                    rule={rule}
                                    index={index}
                                    isDragged={draggedItem === index}
                                    onMouseDown={() => handleDragStart(index)}
                                    imageWidth={imageRef.current?.offsetWidth || 0}
                                    imageHeight={imageRef.current?.offsetHeight || 0}
                                />
                            ))}
                        </div>
                        
                        <div className="mt-3">
                            <div className="d-flex flex-wrap gap-2">
                                {LABEL_TEMPLATES.map((template, index) => {
                                    const isDisabled = labelExists(template.sourceIdentifier);
                                    return (
                                        <button 
                                            key={index}
                                            className={`btn btn-sm ${isDisabled ? 'btn-secondary' : 'btn-outline-primary'}`}
                                            onClick={() => addNewLabel(template)}
                                            disabled={isDisabled}
                                            title={isDisabled ? 'Etykieta już istnieje' : 'Dodaj etykietę'}
                                        >
                                            + {template.sourceIdentifier}
                                            {template.textDirection === 'VERTICAL' && ' (↑)'}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    
                    <div className="col-md-4">
                        <h5>Właściwości rysunku</h5>
                        <div className="mb-3">
                            <div><strong>ID:</strong> {techDraw.id}</div>
                            <div className="mb-2">
                                <strong>Nazwa:</strong>
                                <input 
                                    type="text" 
                                    className="form-control mt-1" 
                                    value={displayName} 
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    placeholder="Wprowadź nazwę rysunku"
                                />
                            </div>
                            <div><strong>Utworzono:</strong> {new Date(techDraw.createdAt).toLocaleString("pl-PL")}</div>
                            <div><strong>Zaktualizowano:</strong> {new Date(techDraw.updatedAt).toLocaleString("pl-PL")}</div>
                        </div>
                        
                        <h5>Reguły etykiet ({rules.length})</h5>
                        <TechDrawRulesTable rules={rules} onRemoveLabel={removeLabel} onUpdateFontSize={updateFontSize} />
                    </div>
                </div>
                
                <div className="d-flex justify-content-between mt-4">
                    <button className="btn btn-secondary" onClick={handleBack}>
                        Powrót do listy
                    </button>
                    
                    <button 
                        className="btn btn-primary" 
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? "Zapisywanie..." : "Zapisz zmiany"}
                    </button>
                </div>

                {/* Preview Section */}
                <div className="mt-5 border-top pt-4">
                    <div className="d-flex justify-content-between mb-3 align-items-center">
                        <h5>Podgląd z przykładowymi wartościami</h5>

                        </div>
                        <button 
                            className="btn btn-outline-primary"
                            onClick={() => setPreviewTimestamp(Date.now())}
                        >
                            Generuj podgląd
                        </button>
                    </div>
                <div>
                    <p className={"p-5"}>
                        <strong>
                            Uwaga - w celu otrzymywania przyzwoitej jakości obrazów, nie nalezy wgrywac obrazów mniejszych niż 800px w dowolnym wymiarze.
                            Czcionki będą skalowane adekwatnie do wymiaru obrazu i mogą stać się niewyraźne na małych obrazach. Podgląd jest generowany na <b>zapisanych</b> ustawieniach obrazu
                            Zalecana wielkość dłuższego boku to 1200 - 1500 pikseli. Ponieważ obrazy na PDF mogą być umieszczane ze stałą szerokością, nie należy wgrywac obrazów o zbyt dużym stosunku wysokości do szerokości (np. 3:1).
                        </strong>
                    </p>
                    
                    {previewObjectUrl ? (
                        <div className="text-center">
                            <img
                                src={previewObjectUrl}
                                alt="Podgląd z etykietami"
                                className="img-fluid border"
                                style={{ maxHeight: '600px', minWidth:"700px" }}
                            />
                            <div className="text-muted mt-2">
                                <small>Uwaga: Podgląd zawiera przykładowe wartości, nie rzeczywiste dane.</small>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center text-muted p-5 border">
                            <p>Kliknij "Generuj podgląd" aby zobaczyć rysunek z etykietami i przykładowymi wartościami.</p>
                        </div>
                    )}
                </div>
            </Card>
        </>
    );
}